import React, { useEffect } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Typography,
} from "antd";
import { ProductPlanItem } from "../../../../api/usim/uploads/products/productPlanItem";

const { TextArea } = Input;

interface ProductManageModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (data: ProductPlanItem) => Promise<void>;
  isUpdate: boolean;
  data?: ProductPlanItem;
  productTypeOptions: { label: string; value: string; }[];
}

export default function ProductManageModal({
  isVisible,
  onClose,
  onSubmit,
  isUpdate,
  data,
  productTypeOptions,
}: ProductManageModalProps) {
  const [form] = Form.useForm();

  // Reset form when modal opens/closes or data changes
  useEffect(() => {
    if (isVisible) {
      if (isUpdate && data) {
        form.setFieldsValue({
          productType: data.type,
          planName: data.planName,
          description: data.description || "",
        });
      } else {
        // Reset form and set default values for add mode
        form.resetFields();
        form.setFieldsValue({
          productType: undefined,
          planName: "",
          description: "",
        });
      }
    }
  }, [isVisible, isUpdate, data, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const productPlan: ProductPlanItem = {
        type: values.productType,
        description: values.description || "",
        planName: values.planName,
      };

      if (isUpdate && data?.Id) {
        productPlan.Id = data.Id;
      }

      await onSubmit(productPlan);
      
      // Reset form after successful submission
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isUpdate ? "Edit Product Plan" : "Add Product Plan"}
      open={isVisible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={isUpdate ? "Update" : "Add"}
      cancelText="Cancel"
      destroyOnClose={true}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          productType: undefined,
          planName: "",
          description: "",
        }}
      >
        <Form.Item
          label="Product Type"
          name="productType"
          rules={[
            { required: true, message: "Product type is required" }
          ]}
        >
          <Select
            options={productTypeOptions}
            placeholder="Select product type"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        
        <Typography.Text type="secondary">
          Please follow our SKU mapping used for Shopee/Lazada
        </Typography.Text>
        <br />
        <br />

        <Form.Item
          label="Plan Name"
          name="planName"
          rules={[
            { required: true, message: "Plan name is required" },
            { whitespace: true, message: "Plan name cannot be empty" }
          ]}
        >
          <Input 
            placeholder="Enter plan name" 
            allowClear
          />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
        >
          <TextArea
            rows={3}
            placeholder="Enter description (optional)"
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}